import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import { Check, ExternalLink, Zap, Clock, Lock, KeyRound, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import ReferralCard from "../components/ReferralCard";
import { DangerZone } from "../components/DangerZone";
import { BillingDates } from "../components/BillingDates";

const PROMO_TXT = {
  it: { title: "Il tuo regalo di benvenuto: -{p}% sul primo mese", sub: "Valido su tutti i piani mensili fino al {d}. Si applica in automatico al checkout.", first: "il primo mese" },
  en: { title: "Your welcome gift: {p}% off your first month", sub: "Valid on all monthly plans until {d}. Applied automatically at checkout.", first: "first month" },
  es: { title: "Tu regalo de bienvenida: -{p}% el primer mes", sub: "Válido en todos los planes mensuales hasta el {d}. Se aplica automáticamente al pagar.", first: "el primer mes" },
  fr: { title: "Votre cadeau de bienvenue : -{p}% le premier mois", sub: "Valable sur tous les forfaits mensuels jusqu'au {d}. Appliqué automatiquement au paiement.", first: "le premier mois" },
  de: { title: "Dein Willkommensgeschenk: {p}% Rabatt im ersten Monat", sub: "Gültig für alle Monatspläne bis {d}. Wird beim Checkout automatisch angewendet.", first: "im ersten Monat" },
};

export default function Account() {
  const { user, checkAuth } = useAuth();
  const { t, lang } = useI18n();
  const hasActivePlan = !!user && ["basic", "plus", "pro"].includes(user.plan);
  const [plans, setPlans] = useState(null);
  const [billing, setBilling] = useState("yearly");
  const [loading, setLoading] = useState(null);
  const [packs, setPacks] = useState([]);
  const [credits, setCredits] = useState({ total_credits: 0, lots: [], expiry_days: 365 });
  const [buyingPack, setBuyingPack] = useState(null);
  const [params] = useSearchParams();
  const navigate = useNavigate();
  // Change password local state
  const [pwOpen, setPwOpen] = useState(false);
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwNew2, setPwNew2] = useState("");
  const [pwSaving, setPwSaving] = useState(false);

  useEffect(() => { api.get("/plans", { params: { lang } }).then((r) => setPlans(r.data)); }, [lang]);
  const [promo, setPromo] = useState(null);
  useEffect(() => { api.get("/payments/trial-promo").then((r) => setPromo(r.data.promo)).catch(() => {}); }, [user]);
  useEffect(() => {
    if (params.get("promo") && promo) { setBilling("monthly"); setTimeout(() => document.getElementById("trial-promo")?.scrollIntoView({ behavior: "smooth" }), 300); }
    // eslint-disable-next-line
  }, [promo]);
  useEffect(() => { api.get("/credits/packs").then((r) => setPacks(r.data.packs || [])); }, []);
  useEffect(() => { api.get("/credits/me").then((r) => setCredits(r.data)).catch(() => {}); }, [user]);
  useEffect(() => {
    const p = params.get("upgrade"); const b = params.get("billing");
    if (p && b && user) { checkout(p, b); navigate("/app/account", { replace: true }); }
    // eslint-disable-next-line
  }, [params, user]);

  const checkout = async (plan, billing) => {
    setLoading(plan);
    try {
      const { data } = await api.post("/payments/checkout", { plan, billing, origin_url: window.location.origin });
      if (data.checkout_url) {
        window.location.href = data.checkout_url;
      } else if (data.modified) {
        // In-place modification with proration — no redirect, refresh the view
        toast.success(t("plan_changed") || "Plan changed. Any credit or charge is prorated by Stripe.");
        await checkAuth();
        setLoading(null);
      }
    } catch (err) {
      toast.error(errorMessage(err, t));
      setLoading(null);
    }
  };

  const buyPack = async (pack_id) => {
    setBuyingPack(pack_id);
    try {
      const { data } = await api.post("/credits/checkout", { pack_id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(errorMessage(err, t));
      setBuyingPack(null);
    }
  };

  const openPortal = async () => {
    try { const { data } = await api.get("/billing/portal"); window.location.href = data.portal_url; }
    catch (err) { toast.error(errorMessage(err, t)); }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (pwNew.length < 8) { toast.error(t("password_too_short") || "Min 8 characters"); return; }
    if (pwNew !== pwNew2) { toast.error(t("passwords_dont_match") || "Passwords don't match"); return; }
    setPwSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: pwCurrent, new_password: pwNew });
      toast.success(t("password_updated") || "Password updated");
      setPwCurrent(""); setPwNew(""); setPwNew2(""); setPwOpen(false);
    } catch (err) { toast.error(errorMessage(err, t)); }
    finally { setPwSaving(false); }
  };

  const isEmailUser = user?.auth_provider === "email";

  if (!plans || !user) return null;

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <div className="text-[10px] uppercase tracking-[0.2em] text-orange-400 font-bold mb-2">{t("account")}</div>
      <h1 className="font-black text-4xl md:text-5xl tracking-tight mb-8">{t("account_title")}</h1>

      <ReferralCard />

      <div className="rounded-md border border-white/10 bg-white/[0.02] p-6 mb-10">
        <div className="grid md:grid-cols-4 gap-6">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{t("current_plan")}</div>
            <div className="font-black text-2xl mt-1" data-testid="current-plan">{plans[user.plan]?.name || t("no_active_plan_label")}</div>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{t("usage")}</div>
            <div className="font-black text-2xl mt-1" data-testid="account-usage">
              {user.monthly_prompts_used} <span className="text-zinc-500 text-base">/ {user.monthly_quota}</span>
            </div>
            {credits.total_credits > 0 && (
              <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mt-1" data-testid="account-credits-total">
                +{credits.total_credits} {t("credits_label") || "credits"}
              </div>
            )}
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">{t("billing")}</div>
            <div className="font-black text-2xl mt-1 capitalize">{user.plan_billing || "—"}</div>
          </div>
          <div className="flex items-end gap-2 flex-wrap">
            {user.plan !== "pending" && (
              <>
                <Button onClick={openPortal} variant="outline" className="rounded-full border-white/10 bg-transparent hover:bg-white/5 text-white" data-testid="portal-btn">
                  {t("portal_btn")} <ExternalLink className="w-3 h-3 ml-2" />
                </Button>
                <Button
                  onClick={openPortal}
                  variant="outline"
                  className="rounded-full border-red-500/30 bg-transparent hover:bg-red-500/10 text-red-300 hover:text-red-200"
                  data-testid="cancel-subscription-btn"
                  title={t("cancel_confirm_msg")}
                >
                  {t("cancel_subscription")}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      <BillingDates />

      {/* Credit Packs */}
      <div className="mb-10" data-testid="credit-packs-section">
        <div className="mb-4 flex items-center gap-3 flex-wrap">
          <Zap className="w-5 h-5 text-emerald-400" />
          <h2 className="font-black text-2xl tracking-tight">{t("credit_packs_title") || "Credit packs"}</h2>
          <span className="text-xs text-zinc-500">· {t("credit_packs_hint") || `Expire after ${credits.expiry_days} days`}</span>
        </div>
        <p className="text-sm text-zinc-400 mb-5 max-w-2xl">{t("credit_packs_desc") || "Extra credits on top of your monthly quota. Consumed automatically after your monthly quota is exhausted."}</p>
        {!hasActivePlan ? (
          <div className="rounded-md border border-amber-500/30 bg-amber-500/[0.06] p-6 flex flex-col sm:flex-row items-start gap-4" data-testid="packs-locked">
            <div className="rounded-full bg-amber-500/15 p-3 shrink-0">
              <Lock className="w-5 h-5 text-amber-400" />
            </div>
            <div className="flex-1">
              <div className="font-bold text-amber-100 mb-1">{t("packs_locked_title") || "Subscribe to unlock credit packs"}</div>
              <p className="text-sm text-zinc-400">{t("packs_locked_desc") || "Credit packs are available only for Basic, Plus and Pro subscribers. Choose a plan below to unlock them."}</p>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {packs.map((pk) => {
              const perPrompt = (pk.price_eur / pk.credits);
              return (
                <div key={pk.pack_id} className="rounded-md border border-white/10 bg-white/[0.02] hover:border-emerald-500/40 transition-colors p-5 flex flex-col" data-testid={`pack-${pk.pack_id}`}>
                  <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-bold mb-1">{t(`pack_label_${pk.pack_id}`) || pk.pack_id}</div>
                  <div className="text-3xl font-black tracking-tight">{pk.credits}</div>
                  <div className="text-xs text-zinc-500 mb-3">{t("credits_label") || "extra credits"}</div>
                  <div className="text-lg font-black text-white mb-1">€{pk.price_eur.toFixed(2)}</div>
                  <div className="text-[10px] text-zinc-500 mb-4">€{perPrompt.toFixed(3)} / {t("prompt_singular") || "credit"}</div>
                  <Button
                    onClick={() => buyPack(pk.pack_id)}
                    disabled={buyingPack === pk.pack_id}
                    data-testid={`buy-pack-${pk.pack_id}`}
                    className="w-full rounded-full h-10 font-bold bg-emerald-500 hover:bg-emerald-600 text-black mt-auto"
                  >
                    {buyingPack === pk.pack_id ? "..." : (t("buy_pack") || "Buy now")}
                  </Button>
                </div>
              );
            })}
          </div>
        )}
        {credits.lots.length > 0 && (
          <div className="mt-5 rounded-md border border-white/10 bg-white/[0.02] p-4" data-testid="credit-lots">
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold mb-3">{t("your_credit_lots") || "Your active credit packs"}</div>
            <div className="space-y-2">
              {credits.lots.map((lot) => (
                <div key={lot.lot_id} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2 text-zinc-300">
                    <Clock className="w-3.5 h-3.5 text-zinc-500" />
                    <span className="font-mono">{lot.credits_remaining} / {lot.credits_initial}</span>
                  </div>
                  <div className="text-xs text-zinc-500">
                    {t("expires_on") || "expires on"} {new Date(lot.expires_at).toLocaleDateString(lang)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Security */}
      <div className="mb-10" data-testid="security-section">
        <h2 className="font-black text-2xl tracking-tight mb-4 flex items-center gap-2">
          <KeyRound className="w-5 h-5 text-orange-400" /> {t("security_title") || "Security"}
        </h2>
        <div className="rounded-md border border-white/10 bg-white/[0.02] p-6">
          {!isEmailUser ? (
            <p className="text-sm text-zinc-400" data-testid="google-only-note">
              {t("google_only_note") || "This account signs in with Google. Password change isn't needed."}
            </p>
          ) : !pwOpen ? (
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <div className="font-semibold text-zinc-100">{t("password") || "Password"}</div>
                <p className="text-xs text-zinc-500 mt-1">{t("password_help") || "Change your login password. Minimum 8 characters."}</p>
              </div>
              <Button variant="outline" onClick={() => setPwOpen(true)} data-testid="open-change-password-btn"
                className="rounded-full border-white/10 bg-transparent hover:bg-white/5 text-white">
                {t("change_password") || "Change password"}
              </Button>
            </div>
          ) : (
            <form onSubmit={changePassword} className="space-y-3 max-w-md" data-testid="change-password-form">
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{t("current_password") || "Current password"}</label>
                <input type="password" required value={pwCurrent} onChange={(e) => setPwCurrent(e.target.value)}
                  data-testid="current-password-input"
                  className="w-full bg-[#16161D] border border-white/10 rounded-md h-11 px-3 mt-1.5 focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{t("new_password") || "New password"}</label>
                <input type="password" required minLength={8} value={pwNew} onChange={(e) => setPwNew(e.target.value)}
                  data-testid="new-password-input"
                  className="w-full bg-[#16161D] border border-white/10 rounded-md h-11 px-3 mt-1.5 focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest text-zinc-400 font-bold">{t("confirm_password") || "Confirm password"}</label>
                <input type="password" required minLength={8} value={pwNew2} onChange={(e) => setPwNew2(e.target.value)}
                  data-testid="confirm-password-input"
                  className="w-full bg-[#16161D] border border-white/10 rounded-md h-11 px-3 mt-1.5 focus:outline-none focus:ring-2 focus:ring-orange-500" />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Button type="submit" disabled={pwSaving} data-testid="save-password-btn"
                  className="rounded-full bg-orange-500 hover:bg-orange-600 text-black font-bold">
                  {pwSaving ? "..." : (t("save") || "Save")}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setPwOpen(false); setPwCurrent(""); setPwNew(""); setPwNew2(""); }}
                  className="rounded-full border-white/10 bg-transparent hover:bg-white/5 text-white">
                  {t("cancel") || "Cancel"}
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>


      {promo && (() => {
        const pt = PROMO_TXT[lang] || PROMO_TXT.en;
        return (
          <div id="trial-promo" className="mb-6 scroll-mt-6 rounded-xl border border-dashed border-orange-500/60 bg-orange-500/[0.07] p-5 flex flex-wrap items-center justify-between gap-4" data-testid="trial-promo-banner">
            <div>
              <div className="font-black text-lg">{pt.title.replace("{p}", promo.percent_off)}</div>
              <div className="text-sm text-zinc-400">{pt.sub.replace("{d}", new Date(promo.expires_at).toLocaleDateString(lang))}</div>
            </div>
            <div className="font-mono font-black text-lg tracking-widest px-4 py-2 rounded-lg bg-[#0E0E12] border border-orange-500/40 text-orange-300" data-testid="trial-promo-code">{promo.code}</div>
          </div>
        );
      })()}
      <div id="account-plans" className="mb-6 flex items-center justify-between flex-wrap gap-4">
        <h2 className="font-black text-2xl tracking-tight">{t("change_plan")}</h2>
        <div className="inline-flex rounded-full border border-white/10 p-1 bg-[#121218]">
          {["monthly", "yearly"].map((b) => (
            <button key={b} onClick={() => setBilling(b)} data-testid={`bill-${b}`} className={`px-5 py-1.5 rounded-full text-sm font-semibold transition-colors duration-200 ${billing === b ? "bg-orange-500 text-black" : "text-zinc-400 hover:text-white"}`}>
              {b === "monthly" ? t("monthly") : t("yearly")}
            </button>
          ))}
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6">
        {["basic","plus","pro"].map((key) => {
          const p = plans[key];
          const monthly = p.monthly_price;
          const annualEquivalent = p.yearly_price / 12;
          const displayPrice = billing === "monthly" ? monthly : annualEquivalent;
          const showStrike = billing === "yearly" && monthly > 0;
          const isCurrent = user.plan === key && user.plan_billing === billing;
          const featured = key === "plus";
          return (
            <div key={key} className={`rounded-md p-6 flex flex-col ${featured ? "border-2 border-orange-500 bg-orange-500/[0.03]" : "border border-white/10 bg-white/[0.02]"}`} data-testid={`account-plan-${key}`}>
              <div className="text-[10px] uppercase tracking-[0.2em] font-bold mb-2 text-orange-400">{p.name}</div>
              {key === "basic" && (
                <div className="inline-flex self-start items-center gap-1.5 text-[10px] font-bold text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-2.5 py-1 mb-3" data-testid="acc-moneyback-badge">
                  <ShieldCheck className="w-3 h-3" /> {t("moneyback_14d") || "14-day money-back"}
                </div>
              )}
              <div className="mb-2 flex items-baseline flex-wrap gap-2">
                {showStrike && (
                  <span className="text-xl text-red-400/70 line-through decoration-red-400/70 decoration-2 font-bold" data-testid={`acc-strike-${key}`}>
                    €{monthly.toFixed(2)}
                  </span>
                )}
                <span className="text-4xl font-black tracking-tight">
                  {displayPrice.toFixed(2)}
                </span>
                <span className="text-zinc-500">€ / {t("per_month").replace("/ ", "")}</span>
              </div>
              {billing === "monthly" && promo && (
                <div className="text-xs font-bold text-orange-300 mb-3" data-testid={`acc-promo-price-${key}`}>
                  €{(monthly * (1 - promo.percent_off / 100)).toFixed(2)} {(PROMO_TXT[lang] || PROMO_TXT.en).first}
                </div>
              )}
              {billing === "yearly" && (
                <div className="text-xs text-zinc-500 mb-5" data-testid={`acc-billed-${key}`}>
                  {t("billed_annually_note")} · €{p.yearly_price.toFixed(2)}
                </div>
              )}
              {billing !== "yearly" && <div className="mb-5" />}
              <ul className="space-y-2 mb-6 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-zinc-300">
                    <Check className="w-4 h-4 text-orange-400 flex-shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              {isCurrent ? (
                <Button disabled className="w-full rounded-full h-11 bg-white/5 text-zinc-400 cursor-default">{t("current")}</Button>
              ) : (
                <Button onClick={() => checkout(key, billing)} disabled={loading === key} data-testid={`upgrade-${key}`} className={`w-full rounded-full h-11 font-bold ${featured ? "bg-orange-500 hover:bg-orange-600 text-black" : "bg-white/10 hover:bg-white/15 text-white border border-white/10"}`}>
                  {loading === key ? "..." : (user.plan === "pending" ? t("activate") : `${t("switch_to")} ${p.name}`)}
                </Button>
              )}
            </div>
          );
        })}
      </div>
      <DangerZone />
    </div>
  );
}
